// La placa de la noche: una imagen para mandar al grupo de WhatsApp.
//
// Se dibuja en un <canvas>, en el celular, sin pedirle nada al servidor más que
// los datos: no hay dependencias nuevas y anda igual con mala señal una vez
// que llegaron. Los colores son los de la FIP (los mismos de src/index.css) y
// las fuentes, las autoalojadas de la app, que ya están en la caché.

import type { Noche } from '../services/api';
import { LOGROS } from '../nucleo/logros';
import { NOMBRE_APP } from '../marca';
import { cargarSimbolo, simboloEnCabecera } from './marcaPlaca';

// Lo de acá arriba lo comparte la placa del mes (dibujoPlacaMes.ts).
export const ANCHO = 1080;
export const MARGEN = 72;
const NAVY = '#002C5B';
export const CELESTE = '#29AAE1';
export const BLANCO = '#FFFFFF';
export const APAGADO = 'rgba(255,255,255,0.62)';
export const BAJA = '#FF8A8A';

export const TITULO = '"Bebas Neue", "Arial Narrow", sans-serif';
export const TEXTO = '"Titillium Web", system-ui, sans-serif';

/** Sin esto, el primer dibujo sale con la fuente de reserva. */
export function cargarFuentes(): Promise<unknown> {
  return Promise.all([
    document.fonts.load(`96px ${TITULO}`),
    document.fonts.load(`600 36px ${TEXTO}`),
    document.fonts.load(`36px ${TEXTO}`),
  ]).catch(() => undefined);
}

export function aPng(lienzo: HTMLCanvasElement): Promise<Blob> {
  return new Promise((ok, mal) =>
    lienzo.toBlob((b) => (b ? ok(b) : mal(new Error('No se pudo armar la imagen'))), 'image/png'),
  );
}

/** Cuántos partidos y cuántos jugadores entran antes de resumir con "+N". */
const MAX_PARTIDOS = 8;
const MAX_MOVIMIENTOS = 10;

export async function dibujarPlaca(
  noche: Noche,
  dia: string,
  link: string,
  apagadas: ReadonlySet<string> = new Set(),
): Promise<Blob> {
  // El navegador carga las fuentes recién cuando algo en pantalla las usa.
  const [, simbolo] = await Promise.all([cargarFuentes(), cargarSimbolo()]);

  // Dos pasadas, como la placa del mes: la primera en un lienzo alto de sobra
  // para medir, la segunda del alto justo. Con secciones que se prenden y se
  // apagan, calcular el alto a mano se desfasa enseguida.
  const borrador = document.createElement('canvas');
  borrador.width = ANCHO;
  borrador.height = 5000;
  const fin = dibujarNoche(borrador.getContext('2d')!, noche, dia, apagadas, simbolo);

  const lienzo = document.createElement('canvas');
  lienzo.width = ANCHO;
  lienzo.height = Math.max(fin + 110, 1350);
  const c = lienzo.getContext('2d')!;
  fondo(c, lienzo.height);
  dibujarNoche(c, noche, dia, apagadas, simbolo);

  // ── Pie ──
  c.fillStyle = APAGADO;
  c.font = `28px ${TEXTO}`;
  c.fillText(link.replace(/^https?:\/\//, '').replace(/\/$/, ''), MARGEN, lienzo.height - 60);

  return aPng(lienzo);
}

/** Todo menos el fondo y el pie. Devuelve hasta dónde llegó. */
function dibujarNoche(
  c: CanvasRenderingContext2D,
  noche: Noche,
  dia: string,
  apagadas: ReadonlySet<string>,
  simbolo: HTMLImageElement | null,
): number {
  const partidos = noche.partidos.slice(0, MAX_PARTIDOS);
  const movimientos = noche.movimientos.slice(0, MAX_MOVIMIENTOS);
  // Los que se repiten (tres roscas en una noche) van en una línea con ×N.
  const veces = new Map<string, number>();
  for (const l of noche.logros) {
    const logro = noche.catalogo_logros?.find((x) => x.id === l.logro_id) ?? LOGROS.find((x) => x.id === l.logro_id);
    if (!logro) continue;
    const linea = `${logro.emoji} ${l.nombre}: ${logro.nombre}`;
    veces.set(linea, (veces.get(linea) ?? 0) + 1);
  }
  const logros = [...veces].map(([linea, n]) => (n > 1 ? `${linea} ×${n}` : linea));

  // ── Cabecera ──
  let y = 120;
  c.fillStyle = CELESTE;
  c.font = `48px ${TITULO}`;
  const inicioMarca = simboloEnCabecera(c, simbolo, MARGEN, y);
  c.fillText(`${NOMBRE_APP.toUpperCase()} · PÁDEL`, inicioMarca, y);
  y += 110;
  c.fillStyle = BLANCO;
  c.font = `120px ${TITULO}`;
  c.fillText(dia.toUpperCase(), MARGEN, y, ANCHO - 2 * MARGEN);
  y += 100;

  // ── Los partidos ──
  y = seccion(c, 'LOS PARTIDOS', y);
  for (const p of partidos) {
    filaPartido(c, p, y);
    y += 78;
  }
  if (noche.partidos.length > MAX_PARTIDOS) {
    c.fillStyle = APAGADO;
    c.font = `30px ${TEXTO}`;
    c.fillText(`y ${noche.partidos.length - MAX_PARTIDOS} partidos más`, MARGEN, y);
    y += 50;
  }

  // ── Cómo se movió la tabla ──
  if (!apagadas.has('tabla')) {
    y += 30;
    y = seccion(c, 'CÓMO SE MOVIÓ LA TABLA', y);
    for (const m of movimientos) {
      const delta = Math.round(m.despues) - Math.round(m.antes);
      c.font = `600 36px ${TEXTO}`;
      c.fillStyle = BLANCO;
      c.fillText(m.nombre, MARGEN, y, 560);
      c.font = `30px ${TEXTO}`;
      c.fillStyle = APAGADO;
      c.textAlign = 'right';
      c.fillText(`${m.ganados}-${m.jugados - m.ganados}`, ANCHO - MARGEN - 190, y);
      c.font = `600 38px ${TEXTO}`;
      c.fillStyle = delta > 0 ? CELESTE : delta < 0 ? BAJA : APAGADO;
      c.fillText(delta > 0 ? `+${delta}` : delta < 0 ? `−${-delta}` : '=', ANCHO - MARGEN, y);
      c.textAlign = 'left';
      y += 58;
    }
    if (noche.movimientos.length > MAX_MOVIMIENTOS) {
      c.fillStyle = APAGADO;
      c.font = `30px ${TEXTO}`;
      c.fillText(`y ${noche.movimientos.length - MAX_MOVIMIENTOS} más`, MARGEN, y);
      y += 46;
    }
  }

  // ── La figura y los logros ──
  if (noche.figuras.length > 0 && !apagadas.has('figura')) {
    y += 40;
    c.fillStyle = CELESTE;
    c.font = `56px ${TITULO}`;
    const ganados = noche.movimientos.find((m) => m.nombre === noche.figuras[0])?.ganados ?? 0;
    c.fillText(
      `⭐ ${noche.figuras.length > 1 ? 'FIGURAS' : 'FIGURA'}: ${noche.figuras.join(' Y ').toUpperCase()} (${ganados} GANADOS)`,
      MARGEN,
      y,
      ANCHO - 2 * MARGEN,
    );
    y += 50;
  }
  // El 1 cambia al cerrar un mes: lo anuncia la primera noche después.
  if (noche.el_1_nuevo && !apagadas.has('el1')) {
    y += 40;
    c.fillStyle = BLANCO;
    c.font = `56px ${TITULO}`;
    c.fillText(
      noche.el_1_nuevo.nombre
        ? `👑 NUEVO “EL 1”: ${noche.el_1_nuevo.nombre.toUpperCase()}`
        : '👑 “EL 1” QUEDÓ VACANTE: HAY EMPATE ARRIBA',
      MARGEN,
      y,
      ANCHO - 2 * MARGEN,
    );
    y += 50;
  }
  if (logros.length > 0 && !apagadas.has('logros')) {
    y += 30;
    y = seccion(c, 'LOGROS DE LA NOCHE', y);
    c.font = `34px ${TEXTO}`;
    c.fillStyle = BLANCO;
    for (const l of logros) {
      c.fillText(l, MARGEN, y, ANCHO - 2 * MARGEN);
      y += 50;
    }
  }
  return y;
}

/** El navy con el resplandor celeste desde la izquierda, como en la FIP. */
export function fondo(c: CanvasRenderingContext2D, alto: number) {
  c.fillStyle = NAVY;
  c.fillRect(0, 0, ANCHO, alto);
  const brillo = c.createRadialGradient(-120, alto * 0.42, 40, -120, alto * 0.42, ANCHO * 1.05);
  brillo.addColorStop(0, 'rgba(41,170,225,0.65)');
  brillo.addColorStop(0.45, 'rgba(41,170,225,0.18)');
  brillo.addColorStop(1, 'rgba(41,170,225,0)');
  c.fillStyle = brillo;
  c.fillRect(0, 0, ANCHO, alto);
}

/** Título de sección con su rayita; devuelve dónde arranca el contenido. */
export function seccion(c: CanvasRenderingContext2D, texto: string, y: number): number {
  c.fillStyle = CELESTE;
  c.fillRect(MARGEN, y - 34, 8, 40);
  c.font = `52px ${TITULO}`;
  c.fillText(texto, MARGEN + 24, y);
  return y + 76;
}

function filaPartido(c: CanvasRenderingContext2D, p: Noche['partidos'][number], y: number) {
  const ganoA = p.games_a > p.games_b;
  const pareja = (equipo: string) =>
    p.jugadores
      .filter((j) => j.equipo === equipo)
      .map((j) => j.nombre)
      .join(' · ');
  const mitad = ANCHO / 2;
  const ancho = mitad - MARGEN - 90;

  c.font = `${ganoA ? '600 ' : ''}34px ${TEXTO}`;
  c.fillStyle = ganoA ? BLANCO : APAGADO;
  c.textAlign = 'right';
  c.fillText(pareja('A'), mitad - 90, y, ancho);

  c.textAlign = 'center';
  c.font = `64px ${TITULO}`;
  c.fillStyle = CELESTE;
  c.fillText(`${p.games_a}-${p.games_b}`, mitad, y + 6);

  c.textAlign = 'left';
  c.font = `${ganoA ? '' : '600 '}34px ${TEXTO}`;
  c.fillStyle = ganoA ? APAGADO : BLANCO;
  c.fillText(pareja('B'), mitad + 90, y, ancho);
}
